import {
  CategoryName,
  DisplayModeName,
  IInParamsValues,
} from '@infotech-soe/metaschema';

import { CategoryOpenMode } from '../../../models/category/category';
import { MenuItemViewModelType } from '../../../services/menu/MenuItemViewModel';
import IUUIDService from '../../../services/uid/IUUIDService';
import UUIDService from '../../../services/uid/UUIDService';
import {
  getBalanceIsChanged,
  getBalanceStateEntity,
} from '../../selectors/balance';
import {
  getContentEntity,
  getLastUsedDisplayModeContentTab,
  getPinnedTabs,
  hasDisplayModeContentTab,
} from '../../selectors/content';
import { getMenuApplication, getMenuItems } from '../../selectors/menu';
import { getGeneralConfig } from '../../selectors/userConfig';
import balance from '../../slices/balance';
import category from '../../slices/category';
import content, { ContentEntityType } from '../../slices/content';
import error from '../../slices/error';
import menu from '../../slices/menu';
import userConfigAction from '../../slices/userConfig';
import {
  createAppListener,
  postDispatch,
  postDispatchAny,
} from '../listenerHelpers';

const contentListener = createAppListener();

const idService: IUUIDService = new UUIDService();

const pendingCategoryOpens: Map<string, () => void> = new Map();
let restoreQueue: Array<{
  category: CategoryName;
  displayMode?: DisplayModeName;
  inParams?: IInParamsValues;
}> = [];

const processNextCategory = (dispatch: (action: any) => void): void => {
  if (restoreQueue.length === 0 || pendingCategoryOpens.size > 0) {
    return;
  }

  const next = restoreQueue.shift();
  if (!next) {
    return;
  }

  const { category: categoryName, displayMode, inParams } = next;

  pendingCategoryOpens.set(categoryName, (): void => {});

  dispatch(
    category.openDisplayMode({
      category: categoryName,
      displayMode,
      ui: { mode: CategoryOpenMode.CONTENT },
      inParams,
      pinned: true,
    })
  );

  setTimeout(() => {
    if (pendingCategoryOpens.has(categoryName)) {
      pendingCategoryOpens.delete(categoryName);
      processNextCategory(dispatch);
    }
  }, 10_000);
};

contentListener.startListening(
  postDispatch(content.close, ({ id, save = true }, state, dispatch) => {
    const entity = getContentEntity(state, id);

    if (entity.type === ContentEntityType.CATEGORY) {
      dispatch(content.closeSuccess({ id }));
      dispatch(category.close({ categoryId: entity.content, save }));
    } else if (entity.type === ContentEntityType.BALANCE_FORM) {
      const isChanged = getBalanceIsChanged(state, entity.content);

      if (isChanged) {
        dispatch(balance.showConfirm({ id: entity.content, isShow: true }));
      } else {
        dispatch(content.closeSuccess({ id }));
        if (save) {
          const { datasetId } = getBalanceStateEntity(state, entity.content);
          dispatch(balance.closeSuccess({ id: entity.content, datasetId }));
        }
      }
    }
  })
);

contentListener.startListening(
  postDispatch(content.addEntity, (contentEntity, _state, dispatch) => {
    const id = idService.generateId();
    dispatch(content.open({ id, contentEntity }));
  })
);

contentListener.startListening(
  postDispatch(
    content.activateDisplayMode,
    ({ category: categoryName, displayMode }, state, dispatch) => {
      if (!hasDisplayModeContentTab(state, categoryName, displayMode)) {
        return;
      }

      const id = getLastUsedDisplayModeContentTab(
        state,
        categoryName,
        displayMode
      );
      dispatch(content.activate({ id }));
    }
  )
);

contentListener.startListening(
  postDispatchAny(
    [menu.getSuccess, userConfigAction.getGeneralSuccess],
    (_payload, state, dispatch) => {
      if (restoreQueue.length > 0 || pendingCategoryOpens.size > 0) {
        return;
      }

      const items = getMenuItems(state);
      if (!Object.keys(items).length) {
        return;
      }

      const application = getMenuApplication(state);
      const config = getGeneralConfig(state);

      const appPinnedTabs = config.pinnedTabs?.[application];
      if (!appPinnedTabs?.length) {
        return;
      }

      restoreQueue = appPinnedTabs.filter(
        ({ category: categoryName, displayMode }) =>
          Object.values(items).some(
            item =>
              item.type === MenuItemViewModelType.DISPLAY_MODE &&
              item.category === categoryName &&
              item.displayMode === displayMode
          )
      );

      processNextCategory(dispatch);
    }
  )
);

contentListener.startListening(
  postDispatch(
    category.openDisplayModeSuccess,
    ({ result }, _state, dispatch) => {
      const categoryName = result.source.category;
      const resolver = pendingCategoryOpens.get(categoryName);
      if (resolver) {
        pendingCategoryOpens.delete(categoryName);
        resolver();
        processNextCategory(dispatch);
      }
    }
  )
);

contentListener.startListening(
  postDispatch(content.togglePin, ({ id }, state, dispatch, prevState) => {
    const prevEntity = prevState.content.entities[id];
    const nextEntity = state.content.entities[id];

    if (!prevEntity || !nextEntity) {
      return;
    }

    if (!prevEntity.pinned && !nextEntity.pinned) {
      dispatch(
        error.showNotification({
          name: 'ValidationError',
          id: 'maxPinnedTabsExceeded',
          code: null,
          message: 'maxPinnedTabsExceeded',
          stack: null,
          info: null,
          logout: false,
        })
      );
      return;
    }

    const application = getMenuApplication(state);
    const pinnedTabs = getPinnedTabs(state);
    dispatch(
      userConfigAction.saveGeneral({
        config: { pinnedTabs: { [application]: pinnedTabs } },
        application,
      })
    );
  })
);

contentListener.startListening(
  postDispatch(content.reorder, (_payload, state, dispatch) => {
    const application = getMenuApplication(state);
    const pinnedTabs = getPinnedTabs(state);
    dispatch(
      userConfigAction.saveGeneral({
        config: { pinnedTabs: { [application]: pinnedTabs } },
        application,
      })
    );
  })
);

export default contentListener.middleware;
